/**
 * The order the writer is about to buy, before any money moves.
 *
 * Renders one or both of two `buildOrder` results — the per-section order and,
 * when it is worth offering, the whole-document order — and lets the writer
 * choose between them. The prices are `doc_pricing.json`'s (via `/doc-types`);
 * tax is added by the provider at checkout, which the copy says.
 *
 * A promo field sits alongside: a valid code (see `POST /promo`) waives the
 * whole bill, which is what internal testing runs on. The parent closes the
 * dialog and proceeds when a code is accepted.
 *
 * The primary button hands the chosen cart to `useCheckout.begin` — the
 * CheckoutDialog then takes over the watching.
 */

import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  makeStyles,
} from "@fluentui/react-components";
import { type FC, useEffect, useState } from "react";

import { fonts, inkColors } from "../tokens";
import { formatUsd } from "./money";
import type { Order } from "./order";

const useStyles = makeStyles({
  surface: { maxWidth: "440px", width: "calc(100vw - 32px)" },
  body: { display: "flex", flexDirection: "column", gap: "10px" },
  scope: { fontFamily: fonts.body, fontSize: "13px", color: inkColors.ink },
  choice: { display: "flex", flexDirection: "column", gap: "8px" },
  choiceBtn: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    alignItems: "center",
    border: "1px solid #ECE7F2",
    borderRadius: "8px",
    padding: "10px 12px",
    cursor: "pointer",
    fontFamily: fonts.body,
    fontSize: "13px",
    color: inkColors.ink,
    background: "transparent",
    textAlign: "left",
    width: "100%",
  },
  choiceOn: { border: "1px solid #7C5CFF", background: "#F6F3FF" },
  choiceMeta: { display: "flex", flexDirection: "column", gap: "2px" },
  choiceHint: { fontSize: "11.5px", color: inkColors.ink3 },
  lines: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    border: "1px solid #ECE7F2",
    borderRadius: "8px",
    padding: "10px 12px",
    maxHeight: "180px",
    overflowY: "auto",
  },
  line: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    fontFamily: fonts.body,
    fontSize: "12.5px",
    color: inkColors.ink,
  },
  lineLabel: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  taxRow: {
    display: "flex",
    justifyContent: "space-between",
    fontFamily: fonts.body,
    fontSize: "11.5px",
    color: inkColors.ink3,
  },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    fontFamily: fonts.body,
    fontSize: "14px",
    fontWeight: 600,
    color: inkColors.ink,
    paddingTop: "6px",
    borderTop: "1px solid #ECE7F2",
  },
  promo: { display: "flex", flexDirection: "column", gap: "4px", paddingTop: "4px" },
  promoRow: { display: "flex", gap: "6px", alignItems: "center" },
  promoInput: { flex: 1 },
  promoMsg: { fontFamily: fonts.body, fontSize: "11.5px" },
  promoOk: { color: "#1B7F3B" },
  promoErr: { color: "#B42318" },
});

export const OrderDialog: FC<{
  /** The per-section order, or null when there is no section order to place. */
  sectionOrder: Order | null;
  /** The whole-document order, offered as an alternative when present. */
  wholeOrder: Order | null;
  /** True while a checkout is being opened, to disable the buttons. */
  busy?: boolean;
  /** Pay for the chosen order. */
  onPay: (order: Order) => void;
  /** Apply a promo code for the chosen scope. Resolves ok=true when it waived payment. */
  onApplyPromo: (code: string, scope: "section" | "document") => Promise<{ ok: boolean; message?: string }>;
  onCancel: () => void;
}> = ({ sectionOrder, wholeOrder, busy = false, onPay, onApplyPromo, onCancel }) => {
  const styles = useStyles();
  const open = sectionOrder !== null || wholeOrder !== null;

  // Default to the section order when there is one; otherwise the whole doc.
  const [scope, setScope] = useState<"section" | "document">(sectionOrder !== null ? "section" : "document");
  const [code, setCode] = useState("");
  const [promoBusy, setPromoBusy] = useState(false);
  const [promoMsg, setPromoMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // Reset the local state whenever a fresh order opens the dialog.
  useEffect(() => {
    if (open) {
      setScope(sectionOrder !== null ? "section" : "document");
      setCode("");
      setPromoMsg(null);
    }
  }, [open, sectionOrder]);

  const active = scope === "section" ? sectionOrder : wholeOrder;
  const showChoice = sectionOrder !== null && wholeOrder !== null;

  const applyPromo = async () => {
    const trimmed = code.trim();
    if (!trimmed || promoBusy) return;
    setPromoBusy(true);
    setPromoMsg(null);
    try {
      const result = await onApplyPromo(trimmed, scope);
      // On success the parent closes the dialog and proceeds; a message here
      // would flash and vanish. Only a failure needs to be shown.
      if (!result.ok) {
        setPromoMsg({ ok: false, text: result.message ?? "That code is not valid." });
      }
    } catch {
      setPromoMsg({ ok: false, text: "Could not check that code. Try again." });
    } finally {
      setPromoBusy(false);
    }
  };

  const busyAll = busy || promoBusy;

  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        if (!data.open) onCancel();
      }}
    >
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle>Start drafting</DialogTitle>
          <DialogContent>
            <div className={styles.body}>
              {showChoice && (
                <div className={styles.choice}>
                  <button
                    type="button"
                    className={`${styles.choiceBtn} ${scope === "section" ? styles.choiceOn : ""}`}
                    onClick={() => setScope("section")}
                  >
                    <span className={styles.choiceMeta}>
                      <span>
                        {sectionOrder?.lines.length === 1
                          ? "This section"
                          : `These ${sectionOrder?.lines.length} sections`}
                      </span>
                      <span className={styles.choiceHint}>Draft only what you pick</span>
                    </span>
                    <span>{formatUsd(sectionOrder?.totalMinor ?? 0)}</span>
                  </button>
                  <button
                    type="button"
                    className={`${styles.choiceBtn} ${scope === "document" ? styles.choiceOn : ""}`}
                    onClick={() => setScope("document")}
                  >
                    <span className={styles.choiceMeta}>
                      <span>The whole document</span>
                      <span className={styles.choiceHint}>Every section, drafted now</span>
                    </span>
                    <span>{formatUsd(wholeOrder?.totalMinor ?? 0)}</span>
                  </button>
                </div>
              )}

              {active !== null && (
                <>
                  {!showChoice && (
                    <span className={styles.scope}>
                      {active.scope === "document"
                        ? `This document is ${formatUsd(active.totalMinor)}.`
                        : `${active.lines.length === 1 ? "This section is" : `These ${active.lines.length} sections are`} ${formatUsd(active.totalMinor)}.`}
                    </span>
                  )}
                  <div className={styles.lines}>
                    {active.lines.map((line) => (
                      <div className={styles.line} key={line.section ?? "document"}>
                        <span className={styles.lineLabel}>
                          {line.section === null ? line.label : `${line.section} · ${line.label}`}
                        </span>
                        <span>{formatUsd(line.priceMinor)}</span>
                      </div>
                    ))}
                  </div>
                  <div className={styles.taxRow}>
                    <span>Tax</span>
                    <span>calculated at payment</span>
                  </div>
                  <div className={styles.totalRow}>
                    <span>Total</span>
                    <span>{formatUsd(active.totalMinor)}</span>
                  </div>
                </>
              )}

              <div className={styles.promo}>
                <div className={styles.promoRow}>
                  <Input
                    className={styles.promoInput}
                    placeholder="Promo code"
                    value={code}
                    disabled={busyAll}
                    onChange={(_, data) => setCode(data.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") void applyPromo();
                    }}
                  />
                  <Button
                    appearance="secondary"
                    disabled={busyAll || code.trim().length === 0}
                    onClick={() => void applyPromo()}
                  >
                    Apply
                  </Button>
                </div>
                {promoMsg !== null && (
                  <span className={`${styles.promoMsg} ${promoMsg.ok ? styles.promoOk : styles.promoErr}`}>
                    {promoMsg.text}
                  </span>
                )}
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onCancel} disabled={busyAll}>
              Not now
            </Button>
            <Button
              appearance="primary"
              onClick={() => active !== null && onPay(active)}
              disabled={busyAll || active === null}
            >
              {active === null ? "Pay and start drafting" : `Pay ${formatUsd(active.totalMinor)} and start drafting`}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default OrderDialog;
